import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateArticleStatus } from "../api/updateArticleStatus";
import { Article, ArticleStatus, UpdateArticleStatusParams } from "../types";
import { articleGroupListQueryKey } from "./useArticleGroupListQuery";

export const useUpdateArticleStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateArticleStatusParams) =>
      updateArticleStatus(params),
    onMutate: async (params) => {
      await queryClient.cancelQueries({
        queryKey: articleGroupListQueryKey({
          brandId: params.brandId,
          categoryId: params.categoryId,
          groupId: params.groupId,
        }),
      });

      const previousArticles = queryClient.getQueryData(
        articleGroupListQueryKey({
          brandId: params.brandId,
          categoryId: params.categoryId,
          groupId: params.groupId,
        }),
      );

      queryClient.setQueryData(
        articleGroupListQueryKey({
          brandId: params.brandId,
          categoryId: params.categoryId,
          groupId: params.groupId,
        }),
        (old: Article[]) => {
          return processUniqueLanguageAvailability(old, params);
        },
      );

      return { previousArticles };
    },
    onError: (err, variables, context) => {
      if (context?.previousArticles) {
        queryClient.setQueryData(
          articleGroupListQueryKey({
            brandId: variables.brandId,
            categoryId: variables.categoryId,
            groupId: variables.groupId,
          }),
          context.previousArticles,
        );
      }
    },
  });
};

export const processUniqueLanguageAvailability = (
  articles: Article[],
  updateArticleData: UpdateArticleStatusParams,
) => {
  return articles.map((article) => {
    if (updateArticleData.articleId === article.id) {
      return { ...article, status: updateArticleData.status };
    }

    if (
      updateArticleData.language === article.language &&
      updateArticleData.status === ArticleStatus.PUBLISHED &&
      updateArticleData.articleId !== article.id
    ) {
      return { ...article, status: ArticleStatus.UNPUBLISHED };
    }

    return article;
  });
};
